<script setup lang="ts">
import SectionStepView from 'modules/Section/views/SectionStepView/SectionStepView.vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserCourseData } from 'modules/Course/useUserCourseData';
import { useTermsOfTransition } from 'shared/composables/useTermsOfTransition';
import { logger } from 'src/shared/utilities/logger';
import { computed, inject, ref } from 'vue';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
import type { UserCourseSectionProgress } from 'src/types/course/course';
import type { Section } from 'modules/Section/models/Section';
import type { TermsOfTransition } from 'src/types/course/termsOfTransition';
import type { UserRole } from 'src/types/User';
import type { ComputedRef } from 'vue';

const $q = useQuasar();
const router = useRouter();
const route = useRoute();
const { userCourseDocId, userSectionDocId } = route.params;
const currentUserRole =
  inject<ComputedRef<UserRole | undefined>>('currentUserRole');
const isSchoolStudent = computed(() => currentUserRole?.value === 'student');

const {
  updateUserCourseRepo,
  updateUserCourseStrapi,
  generateUserSection,
  currentCourseProgress,
  sectionByUserSectionDocId,
  getCourseSectionsTermsOfTransition,
  userSectionByUserSectionId,
  nextCourseSection,
  removeStrapiUserSection,
  removeRepoUserSection,
  isCompletedCourse,
  handleCourseCompletion,
} = useUserCourseData({ userCourseDocId: userCourseDocId as string });

async function resetuserSectionForRedo(
  section: Section,
  sections: UserCourseSectionProgress[],
) {
  if (!currentCourseProgress.value) return;

  // 1. Delete the userSection from strapi
  if (await removeStrapiUserSection(userSectionDocId as string)) {
    // 2. Remove the userSection from the userSectionRepo
    removeRepoUserSection(userSectionDocId as string);
  }

  // 3. Remove the SectionProgress from userCourseProgress
  currentCourseProgress.value.sections = sections.filter(
    (s) => s.section_doc_id !== section.documentId,
  );

  updateUserCourseRepo({
    progress: currentCourseProgress.value,
  });

  // 4. Generate a new user section
  // TODO: This is to be checked, as it fails.
  const userSectionId = await generateUserSection(section?.documentId ?? '');
  if (!userSectionId) throw new Error('Could not generate user section');

  // 5. Redirect to the new user section route
  await router.replace({
    name: 'course.sections.section',
    params: {
      userCourseDocId,
      userSectionId,
      sectionDocId: section.documentId,
    },
  });
}

const termsOfTransition = ref<TermsOfTransition | null | undefined>(null);

const handleSectionCompleted = (score: number, accumulatedTime: number) => {
  if (!currentCourseProgress.value) return;
  const section = sectionByUserSectionDocId(userSectionDocId as string);
  if (!section || !section.documentId) return;

  // Get existing sections or initialize empty array
  const sections = currentCourseProgress.value.sections ?? [];

  // Find section index if it exists
  const sectionIndex = sections.findIndex(
    (s) => s.section_doc_id === section.documentId,
  );

  // Already handled: don't score, redo or transition this section twice. The
  // score is read as a number rather than for truthiness — a section honestly
  // scored 0 is recorded, and treating it as unhandled re-opened the redo
  // dialog and reset the learner's section again on every return.
  const recordedSection =
    sectionIndex >= 0 ? sections[sectionIndex] : undefined;
  if (
    typeof recordedSection?.score === 'number' ||
    recordedSection?.completion_date
  ) {
    return;
  }

  // check if terms of transition are met
  const { getTermsOfTransition } = useTermsOfTransition(
    getCourseSectionsTermsOfTransition(section.id),
  );

  termsOfTransition.value = getTermsOfTransition(score);

  if (termsOfTransition.value?.action === 'Redo_Current') {
    console.log('Redo Current');
    $q.dialog({
      title: t('REDO_SECTION'),
      message: t(
        'YOU_DID_NOT_MEET_THE_TERMS_OF_TRANSITION_FOR_THE_SECTION_REDO',
      ),
      persistent: true,
      ok: {
        label: t('OKAY'),
        color: 'primary',
      },
    }).onOk(() => void resetuserSectionForRedo(section, sections));
    // }).onOk(() => console.log('Redo Current OK'));

    return;
  }

  // Anything that is not a redo moves the course on. Returning here instead
  // left the learner on a finished section the course never advanced past —
  // Skip and End_All are passing actions, and Go_To_Previous has no meaning
  // between sections, so none of them may stall the flow.
  if (termsOfTransition.value?.action !== 'Go_To_Next') {
    logger.log('warn', 'CourseStepView: advancing on a non-Go_To_Next rule', {
      module: 'Course',
      action: 'section-completion',
      transitionAction: termsOfTransition.value?.action ?? 'unresolved',
      sectionDocId: section.documentId,
    });
  }

  // Create updated section data
  const sectionToUpdate = {
    section_doc_id: section.documentId,
    user_section_id: Number(
      userSectionByUserSectionId(userSectionDocId as string)?.id,
    ),
    score,
    time_accumulator: accumulatedTime,
    completion_date: new Date().toISOString(),
    start_date: new Date().toISOString(),
  } as const;

  // Update or add section
  if (sectionIndex >= 0) {
    sections[sectionIndex] = { ...sectionToUpdate, ...sections[sectionIndex] };
  } else {
    sections.push(sectionToUpdate);
  }

  // Update course progress with all sections
  updateUserCourseRepo({
    progress: {
      ...currentCourseProgress.value,
      sections,
    },
  });

  void updateUserCourseStrapi({
    progress: {
      ...currentCourseProgress.value,
      sections,
    },
  });
};

async function goToNextSection() {
  if (isCompletedCourse.value) {
    console.log('Course Completed');
    // Handled by the watcher
    await handleCourseCompletion();
    $q.dialog({
      title: t('COURSE_COMPLETED') + ' 🎉',
      message: t('YOU_HAVE_COMPLETED_THIS_COURSE_BACK_TO_COURSES'),
      persistent: true,
      ok: {
        label: t('OKAY'),
        color: 'secondary',
        'text-color': 'dark',
        unelevated: true,
        class: 'text-bold q-mx-auto rounded-borders-sm',
        padding: 'sm lg',
      },
      class: 'rounded-borders-lg shadow-primary-sm text-center',
    }).onOk(() => {
      void router.push({
        name: isSchoolStudent.value ? 'course.my.student' : 'course.my',
      });
    });
  }

  const nextSection = nextCourseSection.value;
  if (!nextSection) return;

  const nextSectionDocId = nextSection.documentId;
  if (!nextSectionDocId) return;

  const nextUserSectionId = await generateUserSection(nextSectionDocId);

  if (!nextUserSectionId) return;

  await router.push({
    name: 'course.sections.section',
    params: {
      userCourseDocId,
      userSectionId: nextUserSectionId,
      sectionDocId: nextSectionDocId,
    },
  });
}
</script>

<template>
  <!-- Remount when the step OR its user-step changes: the engine reads the
       user-step id once (a replaced user-step must load fresh). -->
  <SectionStepView
    :key="`${$route.params?.stepDocId}:${$route.params?.userStepId}`"
    :course-section-terms-of-transition-action="termsOfTransition?.action"
    @section:completed="handleSectionCompleted"
    @section:transition="goToNextSection"
  />
</template>
